import { AnimatePresence, motion } from 'motion/react'
import { useEffect } from 'react'
import clsx from 'clsx'
import Button from '../ui/buttons/Button'
import ButtonIcon from '../ui/buttons/ButtonIcon'
import IconClose from '../ui/icons/IconClose'
import IconReload from '../ui/icons/IconReload'
import IconShare from '../ui/icons/IconShare'

type PostShareOverlayProps = {
    open: boolean
    onClose: () => void
    onReplay: () => void
    onShareFriends: () => void
}

const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.2, ease: 'easeOut' as const } },
    exit: { opacity: 0, transition: { duration: 0.15, ease: 'easeIn' as const } },
}

const panelVariants = {
    hidden: { opacity: 0, scale: 0.9, y: 24 },
    visible: {
        opacity: 1,
        scale: 1,
        y: 0,
        transition: {
            type: 'spring' as const,
            stiffness: 380,
            damping: 22,
            mass: 0.8,
        },
    },
    exit: { opacity: 0, scale: 0.92, y: 16, transition: { duration: 0.18 } },
}

export default function PostShareOverlay({ open, onClose, onReplay, onShareFriends }: PostShareOverlayProps) {
    useEffect(() => {
        if (!open) return
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [open, onClose])

    return (
        <AnimatePresence>
            {open ? (
                <motion.div
                    data-ui-priority
                    className='z-50 absolute inset-0 flex justify-center items-center bg-black/55 backdrop-blur-sm pointer-events-auto px-4'
                    variants={overlayVariants}
                    initial='hidden'
                    animate='visible'
                    exit='exit'
                    role='dialog'
                    aria-modal='true'
                    aria-label='Neue Sammelkarte erspielen'
                >
                    <div className='relative pt-[132px]'>
                        <div className='absolute top-0 left-1/2 -translate-x-1/2 w-[190px] h-[190px] pointer-events-none overflow-hidden z-10'>
                            <img
                                src='/images/cards-fan.png'
                                alt=''
                                className='w-full h-full object-contain object-bottom'
                            />
                        </div>

                        <motion.div
                            className={clsx(
                                'relative flex flex-col items-center pattern-background',
                                'bg-white-stroke-even-darker border border-black-stroke drop-shadow-2-black-stroke',
                                'gap-4 px-6 py-12 rounded-[32px] w-full max-w-xs',
                            )}
                            variants={panelVariants}
                        >
                            <p className='font-nunito text-[16px] text-center leading-[22px] tracking-[-0.32px]'>
                                <span className='font-black text-white'>Es gibt 4 verschiedene Sammelkarten.</span>
                                {' '}
                                <br />
                                <span className='font-bold text-white'>Welche erspielst du als Nächstes?</span>
                            </p>

                            <div className='flex flex-col gap-2 w-full'>
                                <Button
                                    type='button'
                                    color='orange'
                                    size='large'
                                    multiline
                                    icon={<IconReload />}
                                    onClick={onReplay}
                                >
                                    {'Nochmal spielen &\nneue Karte erhalten'}
                                </Button>
                                <Button
                                    type='button'
                                    color='white'
                                    icon={<IconShare />}
                                    onClick={onShareFriends}
                                >
                                    Freunden zeigen
                                </Button>
                            </div>
                        </motion.div>

                        <div className='left-1/2 absolute -translate-x-1/2 -bottom-6'>
                            <ButtonIcon
                                color='red'
                                variant='standalone'
                                popupCloseSound
                                icon={<IconClose width='100%' height='100%' />}
                                onClick={onClose}
                                aria-label='Schließen'
                            />
                        </div>
                    </div>
                </motion.div>
            ) : null}
        </AnimatePresence>
    )
}
